import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

interface PDFReaderProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PDFReader: React.FC<PDFReaderProps> = ({ isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  
  useEffect(() => {
    // Detección más robusta de móvil
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|phone|tablet/i.test(userAgent);
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const smallScreen = window.innerWidth <= 768;
      
      return isMobileDevice || (hasTouch && smallScreen);
    };
    
    setIsMobile(checkMobile());
  }, []);
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  
  if (!isOpen) return null;

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(3, prev + 0.2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(0.5, prev - 0.2));
  };

  const handleMobileOpen = () => {
    // Para móviles, usar window.open directamente
    const pdfWindow = window.open('/11000 Dante Zetta_.pdf', '_blank');
    if (!pdfWindow) {
      // Si el popup es bloqueado, crear link temporal
      const link = document.createElement('a');
      link.href = '/11000 Dante Zetta_.pdf';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    onClose(); // Cerrar modal después de abrir PDF
  };

  return (
    <div 
      className="pdf-reader-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="pdf-reader-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pdf-reader-header">
          <h3>11000 - Dante Zetta</h3>
          <div className="pdf-controls">
            <button onClick={handleZoomOut} title="Reducir zoom">
              <ZoomOut size={20} />
            </button>
            <span className="zoom-level">{Math.round(zoom * 100)}%</span>
            <button onClick={handleZoomIn} title="Aumentar zoom">
              <ZoomIn size={20} />
            </button>
            <button onClick={onClose} className="close-btn" title="Cerrar">
              <X size={24} />
            </button>
          </div>
        </div>
        
        <div className="pdf-viewer">
          {isMobile ? (
            // Móvil: Mostrar opciones directas
            <div className="mobile-pdf-options">
              <div className="mobile-pdf-content">
                <h4>📖 11000 - Dante Zetta</h4>
                <p>En dispositivos móviles, el PDF se abre mejor en una nueva ventana para una experiencia óptima de lectura.</p>
                
                <div className="mobile-pdf-buttons">
                  <button 
                    onClick={handleMobileOpen}
                    className="pdf-open-btn primary"
                  >
                    📄 Abrir PDF
                  </button>
                  
                  <a
                    href="/11000 Dante Zetta_.pdf"
                    download="11000-Dante-Zetta.pdf"
                    className="pdf-download-btn secondary"
                  >
                    ⬇️ Descargar PDF
                  </a>
                </div>
                
                <p className="mobile-hint">
                  💡 Una vez abierto, puedes usar los gestos de tu dispositivo para hacer zoom y navegar.
                </p>
              </div>
            </div>
          ) : (
            // Desktop: Iframe tradicional
            <iframe
              src={`/11000 Dante Zetta_.pdf#page=${currentPage}&zoom=${zoom * 100}`}
              className="pdf-iframe"
              title="PDF Viewer"
              onError={() => setPdfError(true)}
            />
          )}
        </div>
        
        {!isMobile && (
          <div className="pdf-navigation">
            <button onClick={handlePreviousPage} disabled={currentPage === 1}>
              <ChevronLeft size={20} />
              Anterior
            </button>
            
            <span className="page-info">
              Página {currentPage}
            </span>
            
            <button onClick={handleNextPage}>
              Siguiente
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};